import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import DrawerCDS, { DrawerSize } from '@sharedCR/coreDesignSystem/Drawer/DrawerCDS';
import TextFieldCDS from '@sharedCR/coreDesignSystem/form/TextFieldCDS';
import PropTypes from 'prop-types';

import { StatusEnum } from 'constants/status';

import { createDrawRequestAction } from 'store/construction/actions';

import InlineNotice from '../components/InlineNotice';
import { ConstructionTestIds } from '../Construction.testIds';
import { formatMoney } from '../Construction.utils';

import {
  createDrawLineFieldsStyles,
  createDrawLineHeaderStyles,
  createDrawLineRowStyles,
  createDrawWrapperStyles,
} from './CreateDrawRequestDrawer.styles';
import {
  getHasOverRemainingLine,
  getHasRequestedLine,
  getInitialDrawDraft,
  getIsLineOverRemaining,
  getPercentComplete,
  getRequestedAmount,
  toDrawRequestPayload,
} from './CreateDrawRequestDrawer.utils';

const CREATE_DRAW_FORM_ID = 'construction-create-draw-form';

const CreateDrawRequestDrawer = ({ applicationId, budgetLines = [], open, onClose, onCreated }) => {
  const dispatch = useDispatch();

  const [draft, setDraft] = useState(getInitialDrawDraft());
  const [retainagePercent, setRetainagePercent] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(getInitialDrawDraft());
      setRetainagePercent('');
      setIsDirty(false);
    }
  }, [open]);

  const onRetainageChange = useCallback(event => {
    setRetainagePercent(event.target.value);
    setIsDirty(true);
  }, []);

  const onRequestedAmountChange = useCallback(
    lineId => event => {
      const { value } = event.target;

      setDraft(previous => ({
        ...previous,
        [lineId]: { ...previous[lineId], requestedAmount: value },
      }));
      setIsDirty(true);
    },
    [],
  );

  const onPercentCompleteChange = useCallback(
    lineId => event => {
      const { value } = event.target;

      setDraft(previous => ({
        ...previous,
        [lineId]: { ...previous[lineId], percentComplete: value },
      }));
      setIsDirty(true);
    },
    [],
  );

  const hasRequestedLine = useMemo(() => getHasRequestedLine(draft), [draft]);
  const hasOverRemainingLine = useMemo(
    () => getHasOverRemainingLine({ draft, budgetLines }),
    [draft, budgetLines],
  );

  const onSubmit = useCallback(
    event => {
      event.preventDefault();

      setIsLoading(true);

      dispatch(
        createDrawRequestAction({
          applicationId,
          payload: toDrawRequestPayload({ budgetLines, draft, retainagePercent }),
        }),
      )
        .then(action => {
          onCreated?.(action?.payload?.id);
          onClose();
        })
        .finally(() => setIsLoading(false));
    },
    [dispatch, applicationId, budgetLines, draft, retainagePercent, onCreated, onClose],
  );

  const content = useMemo(
    () => (
      <Box
        component="form"
        id={CREATE_DRAW_FORM_ID}
        sx={createDrawWrapperStyles}
        onSubmit={onSubmit}
      >
        <TextFieldCDS
          dataTestId={ConstructionTestIds.CREATE_DRAW_RETAINAGE_FIELD}
          label="Retainage %"
          name="create-draw-retainage-percent"
          value={retainagePercent}
          onChange={onRetainageChange}
        />
        {budgetLines.map(line => {
          const isOverRemaining = getIsLineOverRemaining({ draft, line });

          return (
            <Box key={line.id} sx={createDrawLineRowStyles}>
              <Box sx={createDrawLineHeaderStyles}>
                <Typography variant="medium14">{line.costCode}</Typography>
                <Typography color="text.secondary" variant="regular12">
                  {`Remaining ${formatMoney(line.remaining)}`}
                </Typography>
              </Box>
              <Box sx={createDrawLineFieldsStyles}>
                <TextFieldCDS
                  dataTestId={ConstructionTestIds.CREATE_DRAW_LINE_AMOUNT_FIELD}
                  error={isOverRemaining}
                  helperText={isOverRemaining ? 'Exceeds remaining budget' : ''}
                  label="Requested Amount"
                  name={`requested-${line.id}`}
                  value={getRequestedAmount(draft, line.id)}
                  onChange={onRequestedAmountChange(line.id)}
                />
                <TextFieldCDS
                  dataTestId={ConstructionTestIds.CREATE_DRAW_LINE_PERCENT_FIELD}
                  label="% Complete"
                  name={`percent-${line.id}`}
                  value={getPercentComplete(draft, line.id)}
                  onChange={onPercentCompleteChange(line.id)}
                />
              </Box>
            </Box>
          );
        })}
        {hasOverRemainingLine && (
          <InlineNotice
            dataTestId={ConstructionTestIds.CREATE_DRAW_OVER_REMAINING_ALERT}
            variant={StatusEnum.WARNING}
          >
            One or more requested amounts exceed the remaining budget for that line. Reduce the
            amount or increase the budget before submitting.
          </InlineNotice>
        )}
      </Box>
    ),
    [
      budgetLines,
      draft,
      retainagePercent,
      onRetainageChange,
      onRequestedAmountChange,
      onPercentCompleteChange,
      hasOverRemainingLine,
      onSubmit,
    ],
  );

  const actions = (
    <Button
      data-testid={ConstructionTestIds.CREATE_DRAW_SAVE_BUTTON}
      disabled={!isDirty || !hasRequestedLine || hasOverRemainingLine || isLoading}
      form={CREATE_DRAW_FORM_ID}
      type="submit"
      variant="contained"
    >
      Submit Draw Request
    </Button>
  );

  return (
    <DrawerCDS
      actions={actions}
      content={content}
      dataTestId={ConstructionTestIds.CREATE_DRAW_DRAWER}
      isDirty={isDirty}
      isLoading={isLoading}
      open={open}
      size={DrawerSize.MEDIUM}
      title="Add Draw Request"
      onClose={onClose}
    />
  );
};

CreateDrawRequestDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  applicationId: PropTypes.string,
  budgetLines: PropTypes.array,
  open: PropTypes.bool,
  onCreated: PropTypes.func,
};

export default memo(CreateDrawRequestDrawer);
